import { useRef, useState } from 'react';
/**
 * S-001 (FR-002): click-to-toggle + shift-click-range multi-select, generalizing the pattern
 * already implemented inline on the tasks board (KanbanBoard.tsx's handleCardClick) for the
 * agents/skills/schedules boards. `group` scopes shift-click ranges to the column/group the
 * click's anchor was set in (mirrors KanbanBoard restricting a range to same-status columns).
 */
export function useBoardSelection() {
    const [selectedIds, setSelectedIds] = useState(new Set());
    const anchorRef = useRef(null);
    function clear() {
        setSelectedIds(new Set());
        anchorRef.current = null;
    }
    /**
     * Call from a card's onClick. `groupIds` is the ordered list of ids in the same group as
     * `id` (e.g. all card ids in the clicked card's column), used to compute a shift-click range.
     * `onPlainClick` runs for an unmodified click (e.g. navigate to the detail view); an
     * unmodified click also selects just that one card (so Delete is enabled immediately after a
     * single click, not just after a Cmd/Ctrl-click) and sets it as the shift-click anchor —
     * without this, a plain click followed by a shift-click on another card had no anchor to
     * range from and collapsed to a 1-item selection instead of the expected range.
     */
    function handleClick(id, group, groupIds, e, onPlainClick) {
        if (e.metaKey || e.ctrlKey) {
            setSelectedIds((prev) => {
                const next = new Set(prev);
                if (next.has(id))
                    next.delete(id);
                else
                    next.add(id);
                return next;
            });
            anchorRef.current = { id, group };
            return;
        }
        if (e.shiftKey) {
            const anchor = anchorRef.current;
            if (anchor && anchor.group === group) {
                const start = groupIds.indexOf(anchor.id);
                const end = groupIds.indexOf(id);
                if (start !== -1 && end !== -1) {
                    const [lo, hi] = start <= end ? [start, end] : [end, start];
                    setSelectedIds(new Set(groupIds.slice(lo, hi + 1)));
                    return;
                }
            }
            setSelectedIds(new Set([id]));
            anchorRef.current = { id, group };
            return;
        }
        setSelectedIds(new Set([id]));
        anchorRef.current = { id, group };
        onPlainClick();
    }
    return { selectedIds, setSelectedIds, handleClick, clear };
}
